import type { FC } from 'react';
import { HStack, ListItem, UnorderedList, VStack } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Button, Header, Text } from '../../../../../ui-components';

export interface ForgotPasswordSubmittedProps {
    email: string;
    setSubmitted: (emailSent: boolean) => void;
}

const ForgotPasswordSubmitted: FC<ForgotPasswordSubmittedProps> = ({ setSubmitted, email }) => (
    <VStack
        data-testid="account-forgot-password-check-your-email-container"
        spacing={8}
        padding={{ base: 4, sm: 8, md: 4 }}
        className="bg-white"
        borderRadius={{ md: 'lg' }}
        boxShadow="0px 2px 10px 0px rgba(65, 64, 66, 0.14)"
    >
        <VStack spacing={4} width="100%" align="start">
            <Header
                as="h4"
                variant="header4"
                style="bold"
                id="account-forgot-password-check-your-email-heading"
            >
                <FormattedMessage
                    id="account.forgot-password.check-your-email"
                    defaultMessage="Check your email"
                />
            </Header>
            <Text id="account-forgot-password-check-your-email-modal-text">
                <FormattedMessage
                    id="account.forgot-password.reset-email-sent-1"
                    defaultMessage="We've sent an email to {email} with instructions for resetting your password."
                    values={{ email }}
                />
            </Text>
            <VStack spacing={1} align="start">
                <Text style="bold">
                    <FormattedMessage
                        id="account.forgot-password.having-trouble"
                        defaultMessage="Having trouble?"
                    />
                </Text>
                <UnorderedList ml={4} marginInlineStart="2em">
                    <ListItem
                        paddingInlineStart={2}
                        sx={{
                            '::marker': {
                                fontSize: '10px',
                            },
                        }}
                    >
                        <FormattedMessage
                            id="account.forgot-password.description-1"
                            defaultMessage="Sit tight. The email may take a few minutes."
                        />
                    </ListItem>
                    <ListItem
                        paddingInlineStart={2}
                        sx={{
                            '::marker': {
                                fontSize: '10px',
                            },
                        }}
                    >
                        <FormattedMessage
                            id="account.forgot-password.description-2"
                            defaultMessage="Make sure you entered the right email and you have an existing account at Sur La Table."
                        />
                    </ListItem>
                    <ListItem
                        paddingInlineStart={2}
                        sx={{
                            '::marker': {
                                fontSize: '10px',
                            },
                        }}
                    >
                        <FormattedMessage
                            id="account.forgot-password.description-3"
                            defaultMessage="Check your spam folder."
                        />
                    </ListItem>
                    <ListItem
                        paddingInlineStart={2}
                        sx={{
                            '::marker': {
                                fontSize: '10px',
                            },
                        }}
                    >
                        <FormattedMessage
                            id="account.forgot-password.description-4"
                            defaultMessage="Password reset links expire after 10 minutes."
                        />
                    </ListItem>
                </UnorderedList>
            </VStack>
            <Text>
                <FormattedMessage
                    id="account.forgot-password.call-us"
                    defaultMessage="If you're still unable to reset your password, please call us at"
                />{' '}
                <Text as="span" style="bold" display="inline">
                    1-317-559-2041.
                </Text>
            </Text>
            <VStack spacing={0} align="start">
                <Text style="bold">
                    <FormattedMessage
                        id="account.forgot-password.our-hours-are"
                        defaultMessage="Our hours are:"
                    />
                </Text>
                <VStack spacing={1} align="start">
                    <Text>
                        <FormattedMessage
                            id="account.forgot-password.mon-fri-hours"
                            defaultMessage="Mon-Fri, 9am-9pm Eastern"
                        />
                    </Text>
                    <Text>
                        <FormattedMessage
                            id="account.forgot-password.sat-sun-hours"
                            defaultMessage="Sat-Sun, 10am-6pm Eastern"
                        />
                    </Text>
                </VStack>
            </VStack>
        </VStack>
        <HStack spacing={8} width="100%" justify="center">
            <Button
                id="account-forgot-password-check-your-email-modal-send-link-button"
                variant="tertiary"
                padding="0px"
                onClick={(): void => setSubmitted(false)}
            >
                <Text as="span" style="bold">
                    <FormattedMessage
                        id="account.forgot-password.send-link-again"
                        defaultMessage="Send link again"
                    />
                </Text>
            </Button>

            <Button
                id="account-forgot-password-check-your-email-modal-back-to-checkout-button"
                variant="tertiary"
                padding="0px"
                onClick={(): void => {
                    // TODO: we should use react-router's history sadly it is not working as expected so we are using the global history.
                    // Seems like window.history.go(-1) is being hijacked by other process (my impression) and it's just reloading the page.
                    window.history.back();
                }}
            >
                <Text as="span" style="bold">
                    <FormattedMessage
                        id="account.forgot-password.submitted.back-to-previous"
                        defaultMessage="Back to previous"
                    />
                </Text>
            </Button>
        </HStack>
    </VStack>
);

ForgotPasswordSubmitted.propTypes = {
    email: PropTypes.string.isRequired,
    setSubmitted: PropTypes.func.isRequired,
};

export { ForgotPasswordSubmitted };
